import { HeadingLBold } from 'components/Typography/Typography';
import { translate } from 'components/Translation';
import CanPerform from 'components/CanPerform/CanPerform';
import AddNumbersDialog from 'views/Organizations/Details/Departments/common/AddNumbersDialog';
import TableHeader, { IButton } from 'components/TableHeader/TableHeader';
import Add from '@material-ui/icons/Add';
import Table from 'components/CustomTable/CustomTable';
import { SingleNumber } from 'utils/api/models/numbers/singleNumber';
import { LinkCell } from 'components/Cells/Cells';
import GridItem from 'components/Grid/GridItem';
import React, { useEffect, useState } from 'react';
import styles from 'assets/jss/material-dashboard-pro-react/views/accounts/departmentDetailsStyle';
import { makeStyles } from '@material-ui/core';
import Account from '@material-ui/icons/AccountCircleOutlined';
import Phone from '@material-ui/icons/PhoneOutlined';
import { SingleDepartment } from 'utils/api/models/departments/singleDepartment';
import { numberDetailsPath as calculatedNumberDetailsPath } from 'utils/paths/numberDetailsPath';
import _ from 'lodash';
import { exportToXLS } from 'utils/export/export';
import moment from 'moment';

// @ts-ignore
const useStyles = makeStyles(styles);

interface IAllUsersProps {
	department: types.organizations.IExtendedDepartmentDetails | null;
	departmentId: string;
	accountId: string;
	departments: SingleDepartment[];
	numbersAdded: () => void;
	deleteNumbers: (numbers: types.organizations.IExtendedSingleNumber[]) => void;
	notAssigned: boolean;
}

const AllUsers = ({
	departmentId,
	accountId,
	departments,
	department,
	numbersAdded,
	deleteNumbers,
	notAssigned,
}: IAllUsersProps) => {
	const [addNumbersDialog, setAddNumbersDialog] = useState<boolean>(false);
	const [selectedNumbers, setSelectedNumbers] = useState<number[]>([]);
	const [searchInputValue, setSearchInputValue] = useState<string>('');
	const [filteredNumbers, setFilteredNumbers] = useState<types.organizations.IExtendedSingleNumber[]>([]);
	const classes = useStyles();

	const userDetailsPath = (userId: string) => {
		return `/dashboard/users/${userId}`;
	};

	const numberDetailsPath = (number: types.organizations.IExtendedSingleNumber) => {
		return calculatedNumberDetailsPath(
			number.accountNum,
			number.billingId || '',
			number.accountType || '',
			number.billingType || '',
			number.upAccountId
		);
	};

	const numbersXlsExport = () => {
		const preparedData = (selectedNumbers.length === 0
			? filteredNumbers
			: _.filter(filteredNumbers, (_number, index: number) => selectedNumbers.indexOf(index) >= 0)
		).map((number: types.organizations.IExtendedSingleNumber) => ({
			'User name': number.username,
			'Phone number': number.accountNum,
		}));

		exportToXLS(preparedData, `numbers ${moment().format('LLL')}`);
	};

	const deleteItems = () => {
		const numbersToDelete = _.filter(
			filteredNumbers,
			(filteredNumber, index) => selectedNumbers.indexOf(index) >= 0
		);
		deleteNumbers([...numbersToDelete]);
	};

	useEffect(() => {
		if (department && department.numbers) {
			const lowercaseSearchInputValue = searchInputValue.toLowerCase();
			const _filteredNumbers = department.numbers.filter((row: SingleNumber) => {
				return ['accountNum', 'username']
					.filter((field: string) => !!row[field as keyof SingleNumber])
					.some((field: string) =>
						row[field as keyof SingleNumber]!.toLowerCase().includes(lowercaseSearchInputValue)
					);
			});
			setFilteredNumbers(_filteredNumbers);
		}
	}, [department, searchInputValue]);

	useEffect(() => {
		setSelectedNumbers([]);
	}, [department, searchInputValue]);

	return (
		<GridItem xs={12} className={classes.usersWrapper} id="department-all-users-table">
			<HeadingLBold color="secondary-light">{translate('departments.all.numbers.users')}</HeadingLBold>
			<CanPerform
				action="departments:add-numbers"
				yes={
					<AddNumbersDialog
						isDialogOpen={addNumbersDialog}
						setIsDialogOpen={(value) => setAddNumbersDialog(value)}
						selectedDepartmentId={departmentId}
						accountId={accountId}
						departments={departments}
						numbersAdded={numbersAdded}
						numbersLength={department?.numbers?.length}
					/>
				}
			/>
			<TableHeader
				setSearchInputValue={(value) => setSearchInputValue(value)}
				searchInputValue={searchInputValue}
				exportToXLS={numbersXlsExport}
				buttons={
					!notAssigned
						? ({
								onClick: () => setAddNumbersDialog(true),
								text: (
									<>
										<Add />
										{translate('add.user')}
									</>
								),
								buttonProps: { withIcon: true },
						  } as IButton)
						: undefined
				}
			/>
			<Table
				total={filteredNumbers.length}
				withHeader
				withCheckbox
				onSelect={(value) => setSelectedNumbers(value)}
				selected={selectedNumbers}
				selectPanel={{
					selectedElements: selectedNumbers.length,
					deleteItems: deleteItems,
					confirmationDialogTitle: translate('department.details.users.table.delete.elements'),
					confirmationDialogBody: translate('department.details.users.table.are.you.sure'),
					downloadMenuItems: [
						{
							name: 'XSLX',
							onClick: numbersXlsExport,
						},
					],
				}}
				tableProps={{
					loading: !department,
					data: filteredNumbers,
					columns: [
						{
							Header: translate('table.phone.number'),
							Cell: ({ original }: { original: types.organizations.IExtendedSingleNumber }) => (
								<LinkCell
									icon={<Phone />}
									path={numberDetailsPath(original)}
									text={original.accountNum}
								/>
							),
							accessor: 'accountNum',
							width: 200,
						},
						{
							Header: translate('table.user.name'),
							Cell: ({ original }: { original: types.organizations.IExtendedSingleNumber }) => (
								<LinkCell
									icon={<Account />}
									path={userDetailsPath(original.userId)}
									text={original.username}
								/>
							),
							accessor: 'username',
							minWidth: 200,
						},
					],
					defaultPageSize: 10,
				}}
			/>
		</GridItem>
	);
};

export default AllUsers;
